import { useState, useEffect, useMemo } from "react";
import { productsApi, type CompanyProduct } from "../api/products";
import { LikeButton } from "@/components/social/LikeButton";
import { useCompany } from "../context/CompanyContext";
import { useBreadcrumbs } from "../context/BreadcrumbContext";
import { Search, Package } from "lucide-react";
import { Input } from "@/components/ui/input";
import { EmptyState } from "../components/EmptyState";
import { PageSkeleton } from "../components/PageSkeleton";

function formatPrice(p: CompanyProduct): string {
  if (p.price > 0) return `$${p.price.toLocaleString()}`;
  return "Price on request";
}

function extractLocation(name: string): string {
  const parts = name.split(" - ");
  return parts.length > 1 ? parts[parts.length - 1] : "";
}

function extractContainerType(name: string): string {
  return name.split(" - ")[0].trim();
}

export default function ProductsPage() {
  const { selectedCompanyId } = useCompany();
  const { setBreadcrumbs } = useBreadcrumbs();
  const [products, setProducts] = useState<CompanyProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    setBreadcrumbs([{ label: "Products" }]);
  }, [setBreadcrumbs]);

  useEffect(() => {
    if (!selectedCompanyId) return;
    setLoading(true);
    productsApi
      .list(selectedCompanyId)
      .then((r) => setProducts(r.products))
      .finally(() => setLoading(false));
  }, [selectedCompanyId]);

  const filtered = useMemo(() => {
    if (!search.trim()) return products;
    const q = search.toLowerCase();
    return products.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        (p.description || "").toLowerCase().includes(q),
    );
  }, [products, search]);

  if (!selectedCompanyId) {
    return <EmptyState icon={Package} message="Select a company to view products." />;
  }

  if (loading) {
    return <PageSkeleton variant="list" />;
  }

  return (
    <div className="w-full max-w-7xl space-y-5">
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-xl font-semibold text-foreground">
          Products <span className="text-muted-foreground">({products.length})</span>
        </h1>
        <div className="relative w-full max-w-xs">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search products..."
            aria-label="Search products"
            className="h-9 pl-9 pr-9 text-sm"
          />
        </div>
      </div>

      {filtered.length === 0 ? (
        <EmptyState icon={Package} message={search ? "No products match this search." : "No products yet."} />
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {filtered.map((p) => {
            const meta = p.metadata as Record<string, unknown> | undefined;
            const imageUrl = (meta?.image_url as string) || "";
            const location = extractLocation(p.name);
            const containerType = extractContainerType(p.name);

            return (
              <div
                key={p.id}
                className="group flex flex-col overflow-hidden rounded-lg border border-border bg-card transition-colors hover:border-foreground/20"
              >
                <div className="relative aspect-video w-full overflow-hidden bg-accent/20">
                  {imageUrl ? (
                    <img
                      src={imageUrl}
                      alt={p.name}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform group-hover:scale-105"
                      onError={(e) => {
                        (e.currentTarget as HTMLImageElement).style.display = "none";
                      }}
                    />
                  ) : (
                    <div className="flex h-full items-center justify-center text-muted-foreground/40">
                      <Package className="h-8 w-8" />
                    </div>
                  )}
                </div>

                <div className="flex flex-1 flex-col gap-1 p-3">
                  <h3 className="truncate text-sm font-medium text-foreground/90" title={p.name}>
                    {containerType}
                  </h3>
                  <p className="text-xs text-muted-foreground">{location}</p>
                  <div className="mt-1 flex items-center justify-between">
                    <span className="text-sm font-semibold text-foreground">
                      {formatPrice(p)}
                    </span>
                    <span className="rounded-full bg-accent px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                      {p.status}
                    </span>
                    <LikeButton productId={p.id} />
                  </div>
                  {p.description && (
                    <p className="mt-1 line-clamp-2 text-[11px] text-muted-foreground/70">
                      {p.description}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
